import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Calendar, Users, MessageSquare, Mail, Phone, MapPin, Plus, Edit, Trash2, X, Filter, Image as ImageIcon, Clock, Video } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "../../../../src/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { RegionSettingsTab } from "./RegionSettingsTab";

interface Region {
  id: string;
  name: string;
  coordinator_name?: string;
  coordinator_email?: string;
  coordinator_phone?: string;
  description?: string;
}

interface Coordinator {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  role: string;
  region_id: string;
  profile_image_url?: string;
}

interface Facilitator {
  name: string;
  email: string;
  phone?: string;
}

interface ConferenceDate {
  date: string;
  start_time: string;
  end_time: string;
}

interface Conference {
  id: string;
  region_id: string;
  conference_name: string;
  description: string | null;
  slogan: string | null;
  conference_dates: ConferenceDate[] | null;
  location: string | null;
  flyer_url: string | null;
  registration_url: string | null;
  status: string;
  is_active: boolean;
  facilitators: Facilitator[];
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  created_at: string;
  updated_at: string;
}

interface PrayerRequest {
  id: string;
  title: string;
  description?: string;
  requester_name?: string;
  status: string;
  created_at: string;
}

interface Activity {
  id: string;
  title: string;
  description?: string;
  activity_type: string;
  schedule?: string;
  schedule_type?: string;
  start_date?: string;
  start_time?: string;
  end_time?: string;
  zoom_link?: string;
  meeting_id?: string;
  passcode?: string;
  facilitators?: any[];
  is_active: boolean;
  region_id: string;
}

interface EnhancedRegionalDashboardProps {
  userRegion: string;
  isGlobalAdmin: boolean;
}

export const EnhancedRegionalDashboard: React.FC<EnhancedRegionalDashboardProps> = ({ 
  userRegion, 
  isGlobalAdmin 
}) => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState(userRegion);
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [filteredConferences, setFilteredConferences] = useState<Conference[]>([]);
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([]);
  const [coordinators, setCoordinators] = useState<Coordinator[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [contactMessages, setContactMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [conferenceDialogOpen, setConferenceDialogOpen] = useState(false);
  const [editingConference, setEditingConference] = useState<Conference | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conferenceToDelete, setConferenceToDelete] = useState<Conference | null>(null);
  const [flyerFile, setFlyerFile] = useState<File | null>(null);
  const [flyerPreview, setFlyerPreview] = useState<string | null>(null);

  // Filter states
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterName, setFilterName] = useState('');

  const [newConference, setNewConference] = useState({
    conference_name: '',
    description: '',
    slogan: '',
    conference_dates: [] as ConferenceDate[],
    location: '',
    registration_url: '',
    status: 'upcoming',
    is_active: true,
    show_registration_link: true,
    facilitators: [] as Facilitator[],
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    loadRegions();
  }, []);

  useEffect(() => {
    if (selectedRegion && selectedRegion !== 'All') {
      loadRegionData();
    }
  }, [selectedRegion]);

  useEffect(() => {
    applyFilters();
  }, [conferences, filterDateFrom, filterDateTo, filterStatus, filterLocation, filterName]);

  // Set default region for global admin to Eastern Region
  useEffect(() => {
    if (isGlobalAdmin && regions.length > 0) {
      if (!selectedRegion || selectedRegion === 'All') {
        const easternRegion = regions.find(r => r.name.toLowerCase().includes('eastern'));
        if (easternRegion) {
          setSelectedRegion(easternRegion.id);
        } else if (regions.length > 0) {
          setSelectedRegion(regions[0].id);
        }
      }
    }
    // Set region for regional admin - always use their assigned region
    if (!isGlobalAdmin && userRegion && regions.length > 0) {
      setSelectedRegion(userRegion);
    }
  }, [regions, isGlobalAdmin, userRegion, selectedRegion]);

  // Handle flyer preview
  useEffect(() => {
    if (flyerFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFlyerPreview(reader.result as string);
      };
      reader.readAsDataURL(flyerFile);
    } else {
      setFlyerPreview(null);
    }
  }, [flyerFile]);

  const loadRegions = async () => {
    try {
      const { data, error } = await supabase.from('regions').select('*').order('name');
      if (error) throw error;
      setRegions(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading regions:', error);
      setLoading(false);
    }
  };

  const loadRegionData = async () => {
    if (!selectedRegion || selectedRegion === 'All') return;

    try {
      const [conferencesResult, prayerResult, coordinatorsResult, activitiesResult, messagesResult] = await Promise.all([
        supabase.from('conferences').select('*').eq('region_id', selectedRegion).order('created_at', { ascending: false }),
        supabase.from('prayer_requests').select('*').eq('region_id', selectedRegion).order('created_at', { ascending: false }),
        // Fetch coordinators from member_roles table for proper role support
        supabase
          .from('member_roles')
          .select(`
            role,
            members!inner(
              id,
              full_name,
              email,
              phone,
              profile_image_url,
              region_id,
              is_active
            )
          `)
          .eq('members.region_id', selectedRegion)
          .in('role', [
            'regional_president',
            'regional_vice_president',
            'regional_secretary',
            'regional_joint_secretary',
            'regional_treasurer'
          ])
          .eq('members.is_active', true),
        supabase.from('activities').select('*').eq('region_id', selectedRegion).eq('is_active', true).order('created_at', { ascending: false }),
        supabase.from('contact_messages').select('*').eq('region_id', selectedRegion).order('created_at', { ascending: false })
      ]);

      if (conferencesResult.error) throw conferencesResult.error;
      if (prayerResult.error) throw prayerResult.error;
      if (coordinatorsResult.error) throw coordinatorsResult.error;
      if (activitiesResult.error) throw activitiesResult.error;
      if (messagesResult.error) throw messagesResult.error;

      setConferences(conferencesResult.data || []);
      setPrayerRequests(prayerResult.data || []);
      
      // Transform and sort coordinators by role priority
      const rolePriority: Record<string, number> = {
        regional_president: 1,
        regional_vice_president: 2,
        regional_secretary: 3,
        regional_joint_secretary: 4,
        regional_treasurer: 5,
      };
      
      const transformedCoordinators = (coordinatorsResult.data || []).map((item: any) => ({
        ...item.members,
        role: item.role
      })).sort((a: any, b: any) => 
        (rolePriority[a.role] || 999) - (rolePriority[b.role] || 999)
      );
      
      setCoordinators(transformedCoordinators);
      setActivities(activitiesResult.data || []);
      setContactMessages(messagesResult.data || []);
    } catch (error) {
      console.error('Error loading region data:', error);
      toast({
        title: "Error",
        description: "Failed to load region data",
        variant: "destructive"
      });
    }
  };

  const applyFilters = () => {
    let filtered = [...conferences];

    // Filter by date range
    if (filterDateFrom || filterDateTo) {
      filtered = filtered.filter(conf => {
        if (!conf.conference_dates || conf.conference_dates.length === 0) return false;
        const confDates = conf.conference_dates.map(d => new Date(d.date));
        const earliest = new Date(Math.min(...confDates.map(d => d.getTime())));
        const latest = new Date(Math.max(...confDates.map(d => d.getTime())));
        
        if (filterDateFrom && earliest < new Date(filterDateFrom)) return false;
        if (filterDateTo && latest > new Date(filterDateTo)) return false;
        return true;
      });
    }

    // Filter by status
    if (filterStatus !== 'all') {
      if (filterStatus === 'active') {
        filtered = filtered.filter(conf => conf.is_active);
      } else if (filterStatus === 'inactive') {
        filtered = filtered.filter(conf => !conf.is_active);
      }
    }

    // Filter by location
    if (filterLocation) {
      filtered = filtered.filter(conf => 
        conf.location?.toLowerCase().includes(filterLocation.toLowerCase())
      );
    }

    // Filter by name
    if (filterName) {
      filtered = filtered.filter(conf => 
        conf.conference_name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    setFilteredConferences(filtered);
  };

  // Calculate conference status based on dates
  const calculateConferenceStatus = (dates: ConferenceDate[]): string => {
    if (!dates || dates.length === 0) return 'upcoming';
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const confDates = dates.map(d => {
      const date = new Date(d.date);
      date.setHours(0, 0, 0, 0);
      return date;
    });
    
    const earliest = new Date(Math.min(...confDates.map(d => d.getTime())));
    const latest = new Date(Math.max(...confDates.map(d => d.getTime())));
    
    if (latest < now) return 'completed';
    if (earliest <= now && latest >= now) return 'ongoing';
    return 'upcoming';
  };

  const handleCreateConference = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let flyerUrl = editingConference?.flyer_url || null;
      
      // Handle file upload if new file selected
      if (flyerFile) {
        // Delete old flyer if updating
        if (editingConference?.flyer_url) {
          const oldPath = editingConference.flyer_url.split('/').pop();
          if (oldPath) {
            await supabase.storage.from('general-media').remove([`conference-flyers/${oldPath}`]);
          }
        }

        const fileExt = flyerFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `conference-flyers/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('general-media')
          .upload(filePath, flyerFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('general-media')
          .getPublicUrl(filePath);
        
        flyerUrl = urlData.publicUrl;
      }

      // Auto-calculate status based on conference dates
      const autoStatus = calculateConferenceStatus(newConference.conference_dates);

      const conferenceData = {
        ...newConference,
        status: autoStatus,
        flyer_url: flyerUrl,
        region_id: selectedRegion,
        facilitators: newConference.facilitators,
        contact_name: newConference.contact_name || null,
        contact_email: newConference.contact_email || null,
        contact_phone: newConference.contact_phone || null
      };

      if (editingConference) {
        const { error } = await supabase
          .from('conferences')
          .update(conferenceData)
          .eq('id', editingConference.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Conference updated successfully"
        });
      } else {
        const { error } = await supabase
          .from('conferences')
          .insert([conferenceData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Conference created successfully"
        });
      }
      
      setConferenceDialogOpen(false);
      setEditingConference(null);
      resetConferenceForm();
      loadRegionData();
    } catch (error: any) {
      console.error('Error saving conference:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEditConference = (conference: Conference) => {
    setEditingConference(conference);
    setNewConference({
      conference_name: conference.conference_name,
      description: conference.description || '',
      slogan: conference.slogan || '',
      conference_dates: conference.conference_dates || [],
      location: conference.location || '',
      registration_url: conference.registration_url || '',
      status: conference.status,
      is_active: conference.is_active,
      show_registration_link: (conference as any).show_registration_link !== false,
      facilitators: conference.facilitators || [],
      contact_name: conference.contact_name || '',
      contact_email: conference.contact_email || '',
      contact_phone: conference.contact_phone || ''
    });
    if (conference.flyer_url) {
      setFlyerPreview(conference.flyer_url);
    }
    setConferenceDialogOpen(true);
  };

  const handleDeleteConference = async () => {
    if (!conferenceToDelete) return;
    
    try {
      if (conferenceToDelete.flyer_url) {
        const fileName = conferenceToDelete.flyer_url.split('/').pop();
        if (fileName) {
          await supabase.storage.from('general-media').remove([`conference-flyers/${fileName}`]);
        }
      }

      const { error } = await supabase
        .from('conferences')
        .delete()
        .eq('id', conferenceToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Conference deleted successfully"
      });
      
      setDeleteDialogOpen(false);
      setConferenceToDelete(null);
      loadRegionData();
    } catch (error: any) {
      console.error('Error deleting conference:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetConferenceForm = () => {
    setNewConference({
      conference_name: '',
      description: '',
      slogan: '',
      conference_dates: [],
      location: '',
      registration_url: '',
      status: 'upcoming',
      is_active: true,
      show_registration_link: true,
      facilitators: [],
      contact_name: '',
      contact_email: '',
      contact_phone: ''
    });
    setFlyerFile(null);
    setFlyerPreview(null);
  };

  const addFacilitator = () => {
    setNewConference(prev => ({
      ...prev,
      facilitators: [...prev.facilitators, { name: '', email: '', phone: '' }]
    }));
  };

  const removeFacilitator = (index: number) => {
    setNewConference(prev => ({
      ...prev,
      facilitators: prev.facilitators.filter((_, i) => i !== index)
    }));
  };

  const updateFacilitator = (index: number, field: 'name' | 'email' | 'phone', value: string) => {
    setNewConference(prev => ({
      ...prev,
      facilitators: prev.facilitators.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const addConferenceDate = () => {
    setNewConference(prev => ({
      ...prev,
      conference_dates: [...prev.conference_dates, { date: '', start_time: '09:00', end_time: '17:00' }]
    }));
  };

  const removeConferenceDate = (index: number) => {
    setNewConference(prev => ({
      ...prev,
      conference_dates: prev.conference_dates.filter((_, i) => i !== index)
    }));
  };

  const updateConferenceDate = (index: number, field: 'date' | 'start_time' | 'end_time', value: string) => {
    setNewConference(prev => ({
      ...prev,
      conference_dates: prev.conference_dates.map((d, i) => 
        i === index ? { ...d, [field]: value } : d
      )
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (date?: string | null, startTime?: string | null, endTime?: string | null) => {
    if (!date) return "Date TBD";
    const parts = String(date).split('-').map(Number);
    if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return String(date);
    const [year, month, day] = parts;
    const d = new Date(year, month - 1, day);
    const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

    const formatTime = (time?: string | null) => {
      if (!time || typeof time !== 'string' || !time.includes(':')) return null;
      const [hours, minutes] = time.split(':').map(Number);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    const s = formatTime(startTime);
    const e = formatTime(endTime);
    if (s && e) return `${dateStr} • ${s} - ${e}`;
    if (s) return `${dateStr} • ${s}`;
    return dateStr;
  };

  if (loading) {
    return <div className="p-4">Loading dashboard...</div>;
  }

  return (
    <Card className="border-border">
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Region Selection - Only for Global Admin */}
          {isGlobalAdmin && (
            <div className="border-b pb-4">
              <div className="flex flex-wrap gap-2">
                {regions.map((region) => (
                  <Button
                    key={region.id}
                    variant={selectedRegion === region.id ? 'default' : 'outline'}
                    onClick={() => setSelectedRegion(region.id)}
                  >
                    {region.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Region display for Regional Admins */}
          {!isGlobalAdmin && selectedRegion && (
            <div className="border-b pb-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  disabled
                >
                  {regions.find(r => r.id === selectedRegion)?.name}
                  <span className="ml-2 text-xs opacity-80">(Your Region)</span>
                </Button>
              </div>
            </div>
          )}

          {selectedRegion && selectedRegion !== 'All' && (
            <>
          {/* Regional Leadership Team Section */}
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Regional Leadership Team
              </CardTitle>
              <CardDescription>
                Meet the leadership team serving this region
              </CardDescription>
            </CardHeader>
            <CardContent>
              {coordinators.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-8">
                  {coordinators.map((coordinator) => (
                    <div key={coordinator.id} className="flex flex-col items-center text-center">
                      <Avatar className="h-16 w-16 mb-2 ring-2 ring-background shadow-lg">
                        <AvatarImage src={coordinator.profile_image_url} alt={coordinator.full_name || ''} />
                        <AvatarFallback className="text-lg font-semibold text-white bg-primary">
                          {(coordinator.full_name || '?')
                            .split(' ')
                            .filter(Boolean)
                            .map((n) => n[0])
                            .join('') || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="space-y-1">
                        <h5 className="font-semibold text-base">{coordinator.full_name || 'Unnamed'}</h5>
                        <p className="text-sm text-muted-foreground capitalize">
                          {(coordinator.role || '').replace(/_/g, ' ').replace('regional ', '')}
                        </p>
                        {coordinator.email && (
                          <p className="text-xs text-primary">
                            <a href={`mailto:${coordinator.email}`} className="hover:underline">
                              {coordinator.email}
                            </a>
                          </p>
                        )}
                        {coordinator.phone && (
                          <p className="text-xs text-muted-foreground">
                            <a href={`tel:${coordinator.phone}`} className="hover:underline">
                              {coordinator.phone}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No coordinator assigned yet.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="conferences" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="conferences">Conferences</TabsTrigger>
              <TabsTrigger value="activities">Activities</TabsTrigger>
              <TabsTrigger value="settings">Region Settings</TabsTrigger>
            </TabsList>

            {/* Conferences Section */}
            <TabsContent value="conferences" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Regional Conferences</h3>
                <Dialog open={conferenceDialogOpen} onOpenChange={(open) => {
                  setConferenceDialogOpen(open);
                  if (!open) {
                    setEditingConference(null);
                    resetConferenceForm();
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Conference
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingConference ? 'Edit Conference' : 'Create New Conference'}</DialogTitle>
                      <DialogDescription>
                        {editingConference ? 'Update conference details' : 'Add a new conference for this region'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateConference} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="conference_name">Conference Name *</Label>
                          <Input
                            id="conference_name"
                            value={newConference.conference_name}
                            onChange={(e) => setNewConference({...newConference, conference_name: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="slogan">Slogan</Label>
                          <Input
                            id="slogan"
                            value={newConference.slogan}
                            onChange={(e) => setNewConference({...newConference, slogan: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={newConference.description}
                          onChange={(e) => setNewConference({...newConference, description: e.target.value})}
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          value={newConference.location}
                          onChange={(e) => setNewConference({...newConference, location: e.target.value})}
                          placeholder="Enter location"
                        />
                      </div>

                      {/* Conference Dates with Times */}
                      <div className="space-y-2">
                        <Label>Conference Dates & Times</Label>
                        <p className="text-xs text-muted-foreground">Add multiple dates with specific times for each day</p>
                        <div className="space-y-3">
                          {newConference.conference_dates.map((dateTime, index) => (
                            <div key={index} className="flex gap-2 items-end p-3 border rounded-lg bg-muted/20">
                              <div className="flex-1 grid grid-cols-3 gap-2">
                                <div>
                                  <Label className="text-xs">Date</Label>
                                  <Input
                                    type="date"
                                    value={dateTime.date}
                                    onChange={(e) => updateConferenceDate(index, 'date', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Start Time</Label>
                                  <Input
                                    type="time"
                                    value={dateTime.start_time}
                                    onChange={(e) => updateConferenceDate(index, 'start_time', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">End Time</Label>
                                  <Input
                                    type="time"
                                    value={dateTime.end_time}
                                    onChange={(e) => updateConferenceDate(index, 'end_time', e.target.value)}
                                  />
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeConferenceDate(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addConferenceDate}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Date
                          </Button>
                        </div>
                      </div>

                      {/* Speakers */}
                      <div className="space-y-2">
                        <Label>Speakers</Label>
                        <div className="space-y-2">
                          {newConference.facilitators.map((facilitator, index) => (
                            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg bg-muted/20">
                              <div className="flex-1 space-y-2">
                                <Input
                                  value={facilitator.name}
                                  onChange={(e) => updateFacilitator(index, 'name', e.target.value)}
                                  placeholder="Speaker name"
                                />
                                <Input
                                  type="email"
                                  value={facilitator.email}
                                  onChange={(e) => updateFacilitator(index, 'email', e.target.value)}
                                  placeholder="speaker@example.com"
                                />
                                <Input
                                  type="tel"
                                  value={facilitator.phone || ''}
                                  onChange={(e) => updateFacilitator(index, 'phone', e.target.value)}
                                  placeholder="(555) 123-4567"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeFacilitator(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addFacilitator}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Speaker
                          </Button>
                        </div>
                      </div>

                      {/* Conference Contact Information */}
                      <div className="space-y-2">
                        <Label>Conference Contact Information</Label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="contact_name" className="text-xs">Contact Name</Label>
                            <Input
                              id="contact_name"
                              placeholder="Contact Name"
                              value={newConference.contact_name}
                              onChange={(e) => setNewConference({...newConference, contact_name: e.target.value})}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="contact_email" className="text-xs">Contact Email</Label>
                            <Input
                              id="contact_email"
                              type="email"
                              placeholder="contact@example.com"
                              value={newConference.contact_email}
                              onChange={(e) => setNewConference({...newConference, contact_email: e.target.value})}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="contact_phone" className="text-xs">Contact Phone</Label>
                            <Input
                              id="contact_phone"
                              type="tel"
                              placeholder="(555) 123-4567"
                              value={newConference.contact_phone}
                              onChange={(e) => setNewConference({...newConference, contact_phone: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="registration_url">Registration URL</Label>
                        <Input
                          id="registration_url"
                          type="url"
                          value={newConference.registration_url}
                          onChange={(e) => setNewConference({...newConference, registration_url: e.target.value})}
                          placeholder="https://..."
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="show_registration_link">Show Registration Link</Label>
                        <div className="flex items-center space-x-2 pt-2">
                          <Switch
                            id="show_registration_link"
                            checked={newConference.show_registration_link !== false}
                            onCheckedChange={(checked) => setNewConference({...newConference, show_registration_link: checked})}
                          />
                          <Label htmlFor="show_registration_link" className="font-normal">
                            {newConference.show_registration_link !== false ? 'Visible on public page' : 'Hidden from public page'}
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Toggle this off to hide the registration button even if a URL is set
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="is_active">Active Status</Label>
                        <div className="flex items-center space-x-2 pt-2">
                          <Switch
                            id="is_active"
                            checked={newConference.is_active}
                            onCheckedChange={(checked) => setNewConference({...newConference, is_active: checked})}
                          />
                          <Label htmlFor="is_active" className="font-normal">
                            {newConference.is_active ? 'Active' : 'Inactive'}
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Status is auto-calculated based on conference dates
                        </p>
                      </div>

                      {/* Flyer Upload with Preview */}
                      <div className="space-y-2">
                        <Label htmlFor="flyer">Conference Flyer</Label>
                        <Input
                          id="flyer"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => setFlyerFile(e.target.files?.[0] || null)}
                        />
                        {flyerPreview && (
                          <div className="mt-2 border rounded-lg p-2 bg-muted/20">
                            <Label className="text-xs text-muted-foreground">Preview:</Label>
                            <img src={flyerPreview} alt="Flyer preview" className="w-full max-h-64 object-contain mt-2 rounded" />
                          </div>
                        )}
                      </div>

                      <DialogFooter>
                        <Button type="submit">{editingConference ? 'Update Conference' : 'Create Conference'}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label className="text-xs">From Date</Label>
                      <Input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">To Date</Label>
                      <Input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Location</Label>
                      <Input
                        placeholder="Search location..."
                        value={filterLocation}
                        onChange={(e) => setFilterLocation(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Conference Name</Label>
                      <Input
                        placeholder="Search name..."
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Conference Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredConferences.length > 0 ? (
                  filteredConferences.map((conference) => (
                    <Card key={conference.id} className="overflow-hidden">
                      {/* Flyer Image */}
                      {conference.flyer_url && (
                        <div className="relative h-48 bg-muted">
                          <img
                            src={conference.flyer_url}
                            alt={conference.conference_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{conference.conference_name}</CardTitle>
                            {conference.slogan && (
                              <p className="text-sm text-muted-foreground italic mt-1">"{conference.slogan}"</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={conference.is_active ? 'default' : 'secondary'}>
                              {conference.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant={conference.status === 'upcoming' ? 'default' : conference.status === 'ongoing' ? 'secondary' : 'outline'}>
                              {conference.status}
                            </Badge>
                          </div>
                        </div>
                        {conference.description && (
                          <CardDescription className="mt-2 line-clamp-2">{conference.description}</CardDescription>
                        )}
                      </CardHeader>

                      <CardContent className="space-y-3">
                        {/* Dates with Times */}
                        {conference.conference_dates && conference.conference_dates.length > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span>Schedule:</span>
                            </div>
                            <div className="ml-6 space-y-1 text-sm">
                              {conference.conference_dates.map((dt, idx) => (
                                <div key={idx} className="text-muted-foreground">
                                  {formatDateTime(dt.date, dt.start_time, dt.end_time)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Location */}
                        {conference.location && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{conference.location}</span>
                          </div>
                        )}

                        {/* Speakers */}
                        {conference.facilitators && conference.facilitators.length > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span>Speakers:</span>
                            </div>
                            <div className="ml-6 space-y-1 text-sm">
                              {conference.facilitators.map((facilitator, idx) => (
                                <div key={idx} className="text-muted-foreground">
                                  {facilitator.name}
                                  {facilitator.email && <span className="ml-2">({facilitator.email})</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          {conference.registration_url && (
                            <Button
                              asChild
                              variant="outline"
                              size="sm"
                              className="flex-1"
                            >
                              <a href={conference.registration_url} target="_blank" rel="noopener noreferrer">
                                Register
                              </a>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditConference(conference)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setConferenceToDelete(conference);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-8 col-span-2">
                    No conferences match the current filters.
                  </p>
                )}
              </div>

              <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Conference</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{conferenceToDelete?.conference_name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteConference} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TabsContent>

            {/* Activities Tab */}
            <TabsContent value="activities">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Regional Activities
                  </CardTitle>
                  <CardDescription>
                    Active bible studies, prayer cells, and fellowship meetings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {activities.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No active activities in this region yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {activities.map((activity) => (
                        <Card key={activity.id} className="border-l-4" style={{ borderLeftColor: 'hsl(var(--primary))' }}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold">{activity.title}</h4>
                                  <Badge variant="outline" className="text-xs">
                                    {activity.activity_type}
                                  </Badge>
                                </div>
                                {activity.description && (
                                  <p className="text-sm text-muted-foreground mb-2">{activity.description}</p>
                                )}
                                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                                  {activity.schedule && (
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-4 w-4" />
                                      <span>{activity.schedule}</span>
                                    </div>
                                  )}
                                  {activity.zoom_link && (
                                    <a 
                                      href={activity.zoom_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-primary hover:underline"
                                    >
                                      <Video className="h-4 w-4" />
                                      Join Meeting
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Prayer Requests Section */}
            <TabsContent value="prayers" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Prayer Requests ({prayerRequests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {prayerRequests.length > 0 ? (
                      prayerRequests.map((prayer) => (
                        <div key={prayer.id} className="p-4 border rounded-lg bg-card">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold">{prayer.title}</h4>
                            <Badge className={getStatusColor(prayer.status)}>
                              {prayer.status}
                            </Badge>
                          </div>
                          {prayer.description && (
                            <p className="text-sm text-muted-foreground mb-2">{prayer.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {prayer.requester_name && <span>By: {prayer.requester_name}</span>}
                            <span>{new Date(prayer.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No prayer requests for this region.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Messages Section */}
            <TabsContent value="messages" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Contact Messages ({contactMessages.length})
                  </CardTitle>
                  <CardDescription>
                    Messages submitted from the contact form for this region
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contactMessages.length > 0 ? (
                      contactMessages.map((message) => (
                        <Card key={message.id} className="p-4 hover:shadow-md transition-shadow">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold">{message.name}</h4>
                                  <Badge variant={
                                    message.status === 'new' ? 'default' : 
                                    message.status === 'in_progress' ? 'secondary' : 
                                    'outline'
                                  }>
                                    {message.status === 'new' ? 'New' : 
                                     message.status === 'in_progress' ? 'In Progress' : 
                                     'Resolved'}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {message.area_of_inquiry}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                                  <a href={`mailto:${message.email}`} className="hover:underline flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {message.email}
                                  </a>
                                  {message.phone && (
                                    <a href={`tel:${message.phone}`} className="hover:underline flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      {message.phone}
                                    </a>
                                  )}
                                  <span className="text-xs">
                                    {new Date(message.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                              <Select
                                value={message.status}
                                onValueChange={async (newStatus) => {
                                  const { error } = await supabase
                                    .from('contact_messages')
                                    .update({ status: newStatus })
                                    .eq('id', message.id);
                                  
                                  if (error) {
                                    toast({
                                      title: "Error",
                                      description: "Failed to update status",
                                      variant: "destructive"
                                    });
                                  } else {
                                    loadRegionData();
                                    toast({
                                      title: "Success",
                                      description: "Status updated"
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="w-32 h-8 bg-background z-50">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-background z-50">
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {message.subject && (
                              <div>
                                <span className="text-sm font-medium">Subject: </span>
                                <span className="text-sm text-muted-foreground">{message.subject}</span>
                              </div>
                            )}
                            <div>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                {message.message}
                              </p>
                            </div>
                          </div>
                        </Card>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No messages for this region.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Region Settings Section */}
            <TabsContent value="settings" className="space-y-4">
              {selectedRegion && selectedRegion !== 'All' && (
                <RegionSettingsTab 
                  regionId={selectedRegion} 
                  isGlobalAdmin={isGlobalAdmin} 
                />
              )}
            </TabsContent>
          </Tabs>
          </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};